import { useNavigate, useSearchParams } from "react-router-dom";
import useRoles from "../../features/Roles/hooks/useRoles";
import { useEffect, useState } from "react";
import { 
  Button, 
  Center, 
  Flex, 
  Heading, 
  VStack, 
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Image,
  Input,
  Select,
  useToast
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { ROUTES } from "../../consts/routes";
import { AdminClawMachinePrize, OlaggModule, PermissionEnum } from "@olagg/db-types";
import Pagination from "@olagg/ui-kit/Pagination";
import usePrizes from "./hooks/usePrizes";
import moment from "moment";

const Actions = ({ prize }: { prize: AdminClawMachinePrize }) => {
  const { missingPermission } = useRoles();
  const { deletePrize } = usePrizes();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();

  const handleViewPrize = () => navigate(`/claw-machine-prizes/${prize.id}`);

  const handleRemovePrize = async () => {
    if (window.confirm('¿Estás seguro que deseas eliminar este premio?')) {
      setIsDeleting(true);
      try {
        await deletePrize(prize.id.toString()).then(() => window.location.reload());
        console.log("Premio eliminado");
      } catch (error) {
        console.error("Error eliminando premio:", error);
        toast({
          title: 'Error',
          colorScheme: 'olaggYellow',
          status: 'error',
          description: error.error || 'Error al eliminar el premio',
          position: 'bottom-right'
        });
      } finally {
        setIsDeleting(false);
      }
    }
  }

  const canEdit = !missingPermission(OlaggModule.ClawMachinePrize, PermissionEnum.EDIT);
  const canDelete = !missingPermission(OlaggModule.ClawMachinePrize, PermissionEnum.DELETE);

  return (
    <Box display="flex" gap="10px" flexWrap="wrap">
      <Button 
        disabled={!canEdit} 
        colorScheme="green"
        size="sm"
        onClick={handleViewPrize}
      >
        Ver
      </Button>
      <Button 
        disabled={!canDelete || isDeleting} 
        colorScheme="red"
        size="sm"
        onClick={handleRemovePrize}
        isLoading={isDeleting}
        loadingText="Eliminando..."
      >
        Eliminar
      </Button>
    </Box>
  )
}

const ClawMachinePrizes = () => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams()
  const { getPrizes, loading } = usePrizes();
  const [prizes, setPrizes] = useState<AdminClawMachinePrize[]>([]);
  const [count, setCount] = useState(0);
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filter, setFilter] = useState({
    title: searchParams.get('title') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
  })
  const { page, limit, title } = filter

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue })

    if (value) searchParams.set(name, value)
    else searchParams.delete(name)
    setSearchParams(searchParams)
  }

  const onClearFilters = () => {
    setFilter({
      title: '',
      page: 1,
      limit: 10,
    });
    setSearchParams(new URLSearchParams());
  }

  const refreshPrizes = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchPrizes = async () => {
    try {
      const response = await getPrizes({ 
        limit, 
        page, 
        title: debouncedTitle || undefined,
      });
      setPrizes(response.clawMachinePrizes);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching prizes:', error);
    }
  };

  // Debounce title filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTitle(title)
    }, 500)

    return () => clearTimeout(timer)
  }, [title])

  useEffect(() => {
    fetchPrizes();
  }, [page, limit, debouncedTitle, refreshTrigger])

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlTitle = searchParams.get('title');

    if (urlPage || urlLimit || urlTitle) {
      setFilter({
        title: urlTitle || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
      });
    }
  }, []);

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Premios del Juego de la Garra
        </Heading>

        <Flex w='full' alignItems='center' gap='5'>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.CLAW_MACHINE_PRIZES.create)}
            isDisabled={missingPermission(OlaggModule.ClawMachinePrize, PermissionEnum.CREATE)}
          >
            Agregar Premio
          </Button>
        </Flex>

        {/* Inline filter */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Título
            </Text>
            <Input
              placeholder="Buscar por título..."
              value={title}
              onChange={(e) => onFilter({ name: 'title', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            />
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          prizes?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white">Título</Th>
                        <Th py={'20px'} color="white">Fecha de Creación</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {prizes?.map((prize, index) => (
                        <Tr key={index} color="white">
                          <Td py={'20px'}>
                            <Flex alignItems={"center"} gap={"4"}>
                              {prize?.imageUrl ? (
                                <Image src={prize.imageUrl} w="40px" h="40px" borderRadius="md" />
                              ) : (
                                <Box w="40px" h="40px" bg="gray.600" borderRadius="md" />
                              )}
                              {prize?.title}
                            </Flex>
                          </Td>
                          <Td py={'20px'}>{prize?.createdAt && moment(prize?.createdAt).format('DD/MM/YYYY')}</Td>
                          <Td py={'20px'}>
                            <Actions prize={prize} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay premios cargados</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  )
}

export default ClawMachinePrizes;